import React, { useEffect, useState, useCallback } from 'react';
import { Plus, X, Check, ChevronDown, ChevronRight, AlertCircle, CheckCircle, Clock, Eye, EyeOff, Trash2, FileText, Pencil } from 'lucide-react';
import AnteprimaDocumento from '../../components/AnteprimaDocumento';
import RicevuteModello from './RicevuteModello';
import PianiRate from './PianiRate';
import SollecitiCorsi from './SollecitiCorsi';
import ScontiCorsi from './ScontiCorsi';
import BonificiCorsi, { BonificiProposti } from './BonificiCorsi';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';
import useIsMobile from '../../utils/useIsMobile';

const fmt = (n) => `€${(n||0).toFixed(2)}`;
const fmtData = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

const STATO_PAG = {
  SALDATO:         { color:'#22C55E', bg:'#22C55E20', label:'Saldato',       icon:'✓' },
  PARZIALE:        { color:'#F59E0B', bg:'#F59E0B20', label:'Parziale',      icon:'◑' },
  DA_PAGARE:       { color:'#EF4444', bg:'#EF444420', label:'Da pagare',     icon:'✗' },
  NESSUN_PACCHETTO:{ color:'#555',    bg:'#55555520', label:'Nessun pacchetto', icon:'—' },
  DOPPIO_INCLUSO:  { color:'#185FA5', bg:'#3B82F620', label:'Doppio incluso', icon:'↔' },
  RIMOSSO:         { color:'#888',    bg:'#88888820', label:'Tolto dal gruppo', icon:'—' },
};

const METODI = ['BONIFICO','CONTANTI','SATISPAY','POS','ALTRO'];
const NOME_METODO = { BONIFICO:'Bonifico', CONTANTI:'Contanti', SATISPAY:'Satispay', POS:'Carta / POS', ALTRO:'Altro' };
// quota tesseramento dovuta: importo per la persona se c'è, altrimenti quello del ciclo (0 se esonerata)
const importoQuota = (q) => !q || q.esonerato ? 0 : (q.importo_personalizzato ?? q.importo);
// Stato rispetto al piano rate (16/10/2026): calcolato dal server in piano_rate
const STATO_RATE = {
  IN_RITARDO: { color:'#DC2626', bg:'#EF44441A', label:'Rata in ritardo' },
  IN_ARRIVO:  { color:'#B45309', bg:'#F59E0B1F', label:'Rata in scadenza' },
  IN_REGOLA:  { color:'#15803D', bg:'#22C55E1A', label:'Rate in regola' },
  SALDATO:    { color:'#15803D', bg:'#22C55E1A', label:'Rate saldate' },
};
const ggIso = (d) => d ? d.slice(0,10).split('-').reverse().join('/') : '—';

// Stessa pagina in due modi (20/10/2026): Pagamento corsi (operatori: corsisti, pagamenti, lezioni)
// e Contabilità corsi (permesso contabilita_corsi: totali della società, rate, bonifici, listino, modello ricevuta).
export default function Corsisti({ contabilita = false }) {
  const { user } = useAuth();
  const isRoot = user?.ruolo === 'ROOT';
  const [cicli, setCicli]           = useState([]);
  const [cicloId, setCicloId]       = useState(null);
  const [corsisti, setCorsisti]     = useState([]);
  const [pacchetti, setPacchetti]   = useState([]);
  const [sconti, setSconti]         = useState([]); // sconti da regolamento, solo annuali (20/10/2026)
  const caricaSconti = useCallback(() => api.get('/planning/sconti').then(r => setSconti(r.data)).catch(() => setSconti([])), []);
  useEffect(() => { caricaSconti(); }, [caricaSconti]);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(false);

  // Filtri
  const [filtroStato, setFiltroStato]   = useState('');
  const [filtroSede, setFiltroSede]     = useState('');
  const [filtroTesto, setFiltroTesto]   = useState('');
  const [filtroRate, setFiltroRate]     = useState('');
  const [pagina, setPagina]             = useState(0); // 50 corsisti per pagina (16/10): ogni riga ha la tendina pacchetti, tutte insieme erano lente

  // UI
  const [tab, setTab]                   = useState(contabilita ? 'andamento' : 'corsisti'); // operatori: 'corsisti' | 'lezioni'; contabilità: 'andamento' | 'rate' | 'bonifici' | 'listino' | 'ricevute'
  const [ricevute, setRicevute]         = useState({ ricevute: [], minorenni: [] }); // numeri già dati + under 18 del ciclo
  const [ricevutaAperta, setRicevutaAperta] = useState(null);
  const [soloU18, setSoloU18]           = useState(false);
  const [aperto, setAperto]             = useState(null);
  const isMobile = useIsMobile();
  const [showFormPag, setShowFormPag]   = useState(null);
  const [formPag, setFormPag]           = useState({ importo:'', data_pagamento: new Date().toISOString().slice(0,10), metodo:'BONIFICO', note:'' });
  const [showFormPacchetto, setShowFormPacchetto] = useState(false);
  const [editingPacchettoId, setEditingPacchettoId] = useState(null);
  const EMPTY_PACCHETTO = { tipo:'CORSO', nome:'', sede_ids:[], categoria:'OPEN', frequenza:'MONO', importo:'', annuale: false, note:'' };
  const [formPacchetto, setFormPacchetto] = useState(EMPTY_PACCHETTO);
  // Lezioni
  const [acquistiLezioni, setAcquistiLezioni] = useState([]);
  const [pacchettiLezioni, setPacchettiLezioni] = useState([]);
  const [atletiAll, setAtletiAll]       = useState([]);
  const [showFormLezione, setShowFormLezione] = useState(false);
  const [formLezione, setFormLezione]   = useState({ atleta_id:'', pacchetto_id:'', note:'' });
  const [apertaLezione, setApertaLezione] = useState(null);
  const [showFormPagLez, setShowFormPagLez] = useState(null);
  const [formPagLez, setFormPagLez]     = useState({ importo:'', metodo:'CONTANTI', data_pagamento: new Date().toISOString().slice(0,10), note:'' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cicliRes, pacRes, atRes] = await Promise.all([
        api.get('/planning/cicli', { params: { leggero: 1 } }), // solo cicli e sedi: la pagina non usa gli atleti del planning
        api.get('/planning/pacchetti'),
        contabilita ? Promise.resolve({ data: [] }) : api.get('/planning/atleti'),
      ]);
      setCicli(cicliRes.data);
      setPacchetti(pacRes.data);
      setAtletiAll(atRes.data);
      const attivo = cicliRes.data[0];
      if (attivo && !cicloId) setCicloId(attivo.id);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadLezioni = useCallback(async () => {
    if (contabilita) return;
    try {
      const [acqRes, pacLezRes] = await Promise.all([
        api.get('/planning/lezioni/acquisti'),
        api.get('/planning/lezioni/pacchetti'),
      ]);
      setAcquistiLezioni(acqRes.data);
      setPacchettiLezioni(pacLezRes.data);
    } catch(e) { console.error(e); }
  }, [contabilita]);

  const loadRicevute = useCallback(() => {
    if (!cicloId || contabilita) return;
    api.get(`/ricevute/ciclo/${cicloId}`).then(r => setRicevute(r.data)).catch(() => setRicevute({ ricevute: [], minorenni: [] }));
  }, [cicloId, contabilita]);
  useEffect(() => { loadRicevute(); }, [loadRicevute]);

  const loadCorsisti = useCallback(async () => {
    if (!cicloId) return;
    setLoading(true);
    try {
      const params = { ciclo_id: cicloId };
      if (filtroStato) params.stato_pagamento = filtroStato;
      if (filtroSede)  params.sede_nome = filtroSede;
      // i totali della società solo in Contabilità corsi, l'elenco solo in Pagamento corsi
      if (contabilita) setStats((await api.get(`/planning/cicli/${cicloId}/stats`)).data);
      else setCorsisti((await api.get('/planning/corsisti', { params })).data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [cicloId, filtroStato, filtroSede, contabilita]);

  // Per ogni corsista: quanti bonifici della banca non assegnati gli somigliano (etichetta sulla riga
  // chiusa, così si sa chi aprire). Solo per chi può collegare e vede tutte le sedi.
  const [bonificiTrovati, setBonificiTrovati] = useState({});
  const vedeBonifici = !contabilita && puoFare(user, 'contabilita_corsi', 'create'); // i bonifici della banca sono di Contabilità corsi
  const loadBonificiTrovati = useCallback(() => {
    if (!cicloId || !vedeBonifici) return;
    api.get('/planning/bonifici-corsi', { params: { ciclo_id: cicloId } }).then(r => {
      const conta = {};
      for (const b of r.data.bonifici || []) for (const c of b.candidati || [])
        if (c.livello === 'ALTA' || c.livello === 'MEDIA') conta[c.atleta_slot_id] = (conta[c.atleta_slot_id] || 0) + 1;
      setBonificiTrovati(conta);
    }).catch(() => setBonificiTrovati({}));
  }, [cicloId, vedeBonifici]);
  useEffect(() => { loadBonificiTrovati(); }, [loadBonificiTrovati, corsisti]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPagina(0); }, [cicloId, filtroStato, filtroSede, filtroTesto, filtroRate, soloU18]);
  useEffect(() => { loadCorsisti(); }, [loadCorsisti]);
  useEffect(() => { loadLezioni(); }, [loadLezioni]);

  const assegnaSconto = async (atletaSlotId, scontoId) => {
    try {
      await api.put(`/planning/atletaslot/${atletaSlotId}/sconto`, { sconto_id: scontoId || null });
      loadCorsisti(); caricaSconti();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  const assegnaPacchetto = async (atletaSlotId, pacchettoId) => {
    try {
      await api.put(`/planning/atletaslot/${atletaSlotId}/pacchetto`, { pacchetto_id: pacchettoId || null });
      loadCorsisti();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  const salvaNewPacchetto = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formPacchetto, importo: parseFloat(formPacchetto.importo) };
      if (editingPacchettoId) await api.put(`/planning/pacchetti/${editingPacchettoId}`, payload);
      else await api.post('/planning/pacchetti', payload);
      setShowFormPacchetto(false);
      setEditingPacchettoId(null);
      setFormPacchetto(EMPTY_PACCHETTO);
      load();
      loadLezioni();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  const startEditPacchetto = (p) => {
    setEditingPacchettoId(p.id);
    setFormPacchetto({
      tipo: p.tipo || 'CORSO', nome: p.nome, sede_ids: p.sede_ids || [],
      categoria: p.categoria || 'OPEN', frequenza: p.frequenza || 'MONO',
      importo: p.importo, annuale: !!p.annuale, note: p.note || '',
    });
    setShowFormPacchetto(true);
  };

  // Solo ROOT: un pacchetto già associato ad atleti ha tutto bloccato tranne la sede (non incide
  // sui report economici) — invio isolato di sede_ids, mai il form intero (altrimenti il backend
  // rifiuterebbe comunque per la presenza di importo/categoria/ecc. nel payload).
  const salvaSedePacchetto = async () => {
    try {
      await api.put(`/planning/pacchetti/${editingPacchettoId}`, { sede_ids: formPacchetto.sede_ids });
      setShowFormPacchetto(false);
      setEditingPacchettoId(null);
      setFormPacchetto(EMPTY_PACCHETTO);
      load();
      loadLezioni();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  const toggleSedePacchetto = (sedeId) => {
    setFormPacchetto(p => ({
      ...p,
      sede_ids: p.sede_ids.includes(sedeId) ? p.sede_ids.filter(id => id !== sedeId) : [...p.sede_ids, sedeId],
    }));
  };

  const toggleAttivoPacchetto = async (p) => {
    try {
      await api.put(`/planning/pacchetti/${p.id}`, { attivo: !p.attivo });
      load();
      loadLezioni();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  const toggleVisibilePacchetto = async (p) => {
    try {
      await api.put(`/planning/pacchetti/${p.id}`, { visibile: !p.visibile });
      load();
      loadLezioni();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  const eliminaPacchetto = async (p) => {
    if (!window.confirm(`Eliminare definitivamente "${p.nome}"? Non è recuperabile.`)) return;
    try {
      await api.delete(`/planning/pacchetti/${p.id}`);
      if (editingPacchettoId === p.id) annullaFormPacchetto();
      load();
      loadLezioni();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  const annullaFormPacchetto = () => {
    setShowFormPacchetto(false);
    setEditingPacchettoId(null);
    setFormPacchetto(EMPTY_PACCHETTO);
  };

  const salvaPagamento = async (atletaSlotId) => {
    try {
      await api.post(`/planning/atletaslot/${atletaSlotId}/pagamenti`, { ...formPag, importo: parseFloat(formPag.importo) });
      setShowFormPag(null);
      setFormPag({ importo:'', data_pagamento: new Date().toISOString().slice(0,10), metodo:'BONIFICO', note:'' });
      loadCorsisti();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  // Prezzo finale per la persona (sconto): motivo obbligatorio, lo vede solo lo staff; vuoto = torna al listino/ciclo
  const cambiaImporto = async (tipo, id, attuale, base) => {
    const nuovo = window.prompt(`Importo finale per questa persona (${tipo === 'pacchetto' ? 'listino' : 'quota del ciclo'} ${fmt(base)}).\nLascia vuoto per tornare a ${fmt(base)}.`, attuale != null ? String(attuale).replace('.', ',') : '');
    if (nuovo === null) return;
    let body = { importo: null };
    if (nuovo.trim()) {
      const motivo = window.prompt('Motivo (lo vede solo lo staff, in ricevuta non compare):');
      if (motivo === null) return;
      if (!motivo.trim()) { alert('Il motivo è obbligatorio.'); return; }
      body = { importo: nuovo.trim().replace(',', '.'), motivo };
    }
    try {
      await api.put(tipo === 'pacchetto' ? `/planning/atletaslot/${id}/importo` : `/planning/quote-tesseramento/${id}/importo`, body);
      loadCorsisti();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  // Esonero quota tesseramento: motivazione obbligatoria (resta scritta sulla riga e nel registro attività)
  const esoneraQuota = async (quota) => {
    let body;
    if (quota.esonerato) {
      if (!window.confirm('Togliere l\'esonero? La quota tornerà dovuta.')) return;
      body = { esonerato: false };
    } else {
      const nota = window.prompt(`Motivo dell'esonero dalla quota tesseramento (${fmt(quota.importo)}):`);
      if (nota === null) return;
      if (!nota.trim()) { alert('Il motivo è obbligatorio.'); return; }
      body = { esonerato: true, nota };
    }
    try {
      await api.put(`/planning/quote-tesseramento/${quota.id}/esonero`, body);
      loadCorsisti();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  const eliminaPagamento = async (pagId) => {
    if (!window.confirm('Eliminare questo pagamento?')) return;
    try {
      await api.delete(`/planning/pagamenti/${pagId}`);
      loadCorsisti();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  const salvaAcquistoLezioni = async (e) => {
    e.preventDefault();
    try {
      await api.post('/planning/lezioni/acquisti', { ...formLezione });
      setShowFormLezione(false);
      setFormLezione({ atleta_id:'', pacchetto_id:'', note:'' });
      loadLezioni();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  const eliminaAcquistoLezioni = async (id) => {
    if (!window.confirm('Eliminare questo acquisto e tutti i pagamenti associati?')) return;
    try {
      await api.delete(`/planning/lezioni/acquisti/${id}`);
      loadLezioni();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  const salvaPagamentoLezione = async (acquistoId) => {
    try {
      await api.post(`/planning/lezioni/acquisti/${acquistoId}/pagamenti`, { ...formPagLez, importo: parseFloat(formPagLez.importo) });
      setShowFormPagLez(null);
      setFormPagLez({ importo:'', metodo:'CONTANTI', data_pagamento: new Date().toISOString().slice(0,10), note:'' });
      loadLezioni();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  const eliminaPagamentoLezione = async (id) => {
    if (!window.confirm('Eliminare questo pagamento?')) return;
    try {
      await api.delete(`/planning/lezioni/pagamenti/${id}`);
      loadLezioni();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  // Sedi disponibili nel ciclo selezionato
  const cicloCorrente = cicli.find(c => c.id === cicloId);
  const sediCiclo = cicloCorrente?.sedi?.map(s => s.nome) || [];
  const sediCicloObjs = cicloCorrente?.sedi || []; // per il selettore sede del listino prezzi

  const pacchettoInModifica = editingPacchettoId ? pacchetti.find(p => p.id === editingPacchettoId) : null;
  const pacchettoBloccato = !!pacchettoInModifica?.n_associazioni;

  // Filtra per testo
  const minorenni = new Set(ricevute.minorenni);
  const numeroRicevuta = Object.fromEntries(ricevute.ricevute.map(r => [r.atleta_id, r]));
  const corsistiFiltrati = corsisti.filter(c =>
    (!filtroTesto || `${c.atleta.cognome} ${c.atleta.nome}`.toLowerCase().includes(filtroTesto.toLowerCase())) &&
    (!soloU18 || minorenni.has(c.atleta_id)) &&
    (!filtroRate || (filtroRate === 'SENZA' ? !c.piano_rate : c.piano_rate?.stato === filtroRate))
  );
  const PER_PAGINA = 50;
  const nPagine = Math.max(1, Math.ceil(corsistiFiltrati.length / PER_PAGINA));
  const paginaOk = Math.min(pagina, nPagine - 1);
  const righePagina = corsistiFiltrati.slice(paginaOk * PER_PAGINA, (paginaOk + 1) * PER_PAGINA);
  const conPiano = corsisti.filter(c => c.piano_rate && !c.rimosso);
  const inRitardo = conPiano.filter(c => c.piano_rate.stato === 'IN_RITARDO');
  const inArrivo = conPiano.filter(c => c.piano_rate.stato === 'IN_ARRIVO');
  const puoCollegare = puoFare(user, 'contabilita_corsi', 'create');

  const selettoreCiclo = (
      <div style={{ display:'flex', gap:'8px', marginBottom:'16px', flexWrap:'wrap', alignItems:'center' }}>
        <span style={s.secLabel}>Ciclo:</span>
        {cicli.map(c => (
          <button key={c.id} onClick={() => setCicloId(c.id)}
            style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'6px 14px', borderRadius:'99px', fontSize: 'var(--font-size-xs)', fontWeight:'500', cursor:'pointer', border: cicloId===c.id?'none':'0.5px solid var(--border2,#2A2A2A)', background: cicloId===c.id?'#F4600C':'var(--bg-input,#1A1A1A)', color: cicloId===c.id?'#fff':'#888' }}>
            {c.nome}
          </button>
        ))}
      </div>
  );

  return (
    <div>
      {/* HEADER */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'20px' }}>
        <div>
          <h1 style={s.title}>{contabilita ? 'CONTABILITÀ CORSI' : 'PAGAMENTO CORSI'}</h1>
          <p style={s.sub}>{contabilita ? 'Andamento degli incassi, piani rate, bonifici e listino per ciclo' : 'Pagamenti dei corsisti e lezioni singole per ciclo'}</p>
        </div>
      </div>

      {/* TAB */}
      <div className="schede-scorrevoli" style={{ display:'flex', gap:'4px', marginBottom:'18px', borderBottom:'1px solid var(--border,#1E1E1E)', paddingBottom:'0' }}>
        {(contabilita
          ? [['andamento','Andamento'],['rate','Piani rate'],['solleciti','Solleciti email'],...(puoCollegare ? [['bonifici','Tutti i bonifici da assegnare']] : []),['listino','Listino e sconti'],['ricevute','Modello ricevuta']]
          : [['corsisti','Corsi'],['lezioni','Lezioni singole aggiuntive']]).map(([key,label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ background:'none', border:'none', cursor:'pointer', padding:'8px 18px', fontSize:'var(--font-size-sm)', fontWeight:'700', color: tab===key?'#F4600C':'#555', borderBottom: tab===key?'2px solid #F4600C':'2px solid transparent', marginBottom:'-1px' }}>
            {label}
          </button>
        ))}
      </div>

      {contabilita && tab === 'rate' && <>{selettoreCiclo}<PianiRate cicloId={cicloId} ciclo={cicloCorrente} puoModificare={puoFare(user, 'contabilita_corsi', 'edit')}/></>}
      {contabilita && tab === 'solleciti' && <>{selettoreCiclo}<SollecitiCorsi cicloId={cicloId} puoInviare={puoFare(user, 'contabilita_corsi', 'edit')} puoCollegare={puoCollegare}/></>}
      {contabilita && tab === 'bonifici' && puoCollegare && <>{selettoreCiclo}<BonificiCorsi cicloId={cicloId} puoCollegare={puoCollegare} onCollegato={loadCorsisti}/></>}

      {contabilita && tab === 'ricevute' && <RicevuteModello puoModificare={puoFare(user, 'contabilita_corsi', 'edit')} puoEliminare={puoFare(user, 'contabilita_corsi', 'delete')} />}

      {/* ═══════════════ ANDAMENTO (solo Contabilità corsi) ═══════════════ */}
      {tab === 'andamento' && <>
      {selettoreCiclo}

      {/* KPI */}
      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr) repeat(3,1fr)', gap:'10px', marginBottom:'16px' }}>
          <div style={s.kpi}><div style={s.kpiV}>{stats.totale_corsisti}</div><div style={s.kpiL}>Corsisti attivi{stats.rimossi > 0 ? ` · ${stats.rimossi} tolti (non conteggiati)` : ''}</div></div>
          <div style={{ ...s.kpi, border:'1px solid #22C55E30' }}><div style={{ ...s.kpiV, color:'#22C55E' }}>{stats.saldati}</div><div style={s.kpiL}>Saldati</div></div>
          <div style={{ ...s.kpi, border:'1px solid #F59E0B30' }}><div style={{ ...s.kpiV, color:'#F59E0B' }}>{stats.parziali}</div><div style={s.kpiL}>Pagamento parziale</div></div>
          <div style={{ ...s.kpi, border:'1px solid #EF444430' }}><div style={{ ...s.kpiV, color:'#EF4444' }}>{stats.da_pagare}</div><div style={s.kpiL}>Da pagare</div></div>
          <div style={s.kpi}><div style={{ ...s.kpiV, fontSize: 'var(--font-size-lg)' }}>{fmt(stats.totale_atteso)}</div><div style={s.kpiL}>Totale atteso</div></div>
          <div style={{ ...s.kpi, border:'1px solid #22C55E30' }}><div style={{ ...s.kpiV, fontSize: 'var(--font-size-lg)', color:'#22C55E' }}>{fmt(stats.totale_incasso)}</div><div style={s.kpiL}>Incassato</div></div>
          <div style={{ ...s.kpi, border:'1px solid #EF444430' }}><div style={{ ...s.kpiV, fontSize: 'var(--font-size-lg)', color:'#EF4444' }}>{fmt(stats.totale_residuo)}</div><div style={s.kpiL}>Residuo</div></div>
        </div>
      )}

      {stats && (stats.tesseramenti > 0 || stats.tesseramenti_esonerati > 0) && (
        <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-muted,#888)', margin:'-8px 0 14px' }}>
          Quote tesseramento assegnate in questo ciclo: <strong>{stats.tesseramenti}</strong> per {fmt(stats.totale_tesseramenti)} (già comprese nel totale atteso)
          {stats.tesseramenti_esonerati > 0 && <> · {stats.tesseramenti_esonerati} esonerat{stats.tesseramenti_esonerati === 1 ? 'o' : 'i'}</>}
        </div>
      )}

      {stats?.rate?.con_piano > 0 && (
        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center', fontSize:'var(--font-size-xs)', margin:'-6px 0 14px' }}>
          <span style={{ color:'var(--text-muted)' }}>Piani rate: {stats.rate.con_piano} corsisti con scadenze</span>
          {stats.rate.in_ritardo > 0 && <span style={{ ...s.btnSec, padding:'4px 10px', color:'#DC2626', cursor:'default' }}>⚠ {stats.rate.in_ritardo} in ritardo · {fmt(stats.rate.scaduto)} scaduti</span>}
          {stats.rate.in_arrivo > 0 && <span style={{ ...s.btnSec, padding:'4px 10px', color:'#B45309', cursor:'default' }}>{stats.rate.in_arrivo} con rata entro 7 giorni</span>}
        </div>
      )}

      {stats?.per_sede?.length > 0 && (
        <div style={{ ...s.formCard, padding:'0', overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'var(--font-size-sm)' }}>
            <thead><tr style={{ textAlign:'left', color:'var(--text-muted,#888)', fontSize:'var(--font-size-xs)' }}>
              {['Sede','Corsisti','Atteso','Incassato','Residuo'].map((h, i) => <th key={h} style={{ padding:'10px 14px', fontWeight:700, textAlign: i ? 'right' : 'left' }}>{h}</th>)}
            </tr></thead>
            <tbody>{stats.per_sede.map(r => (
              <tr key={r.sede} style={{ borderTop:'1px solid var(--border,#1E1E1E)' }}>
                <td style={{ padding:'9px 14px', fontWeight:600 }}>{r.sede}</td>
                <td style={{ padding:'9px 14px', textAlign:'right' }}>{r.corsisti}</td>
                <td style={{ padding:'9px 14px', textAlign:'right' }}>{fmt(r.atteso)}</td>
                <td style={{ padding:'9px 14px', textAlign:'right', color:'#22C55E' }}>{fmt(r.incassato)}</td>
                <td style={{ padding:'9px 14px', textAlign:'right', color: r.residuo > 0 ? '#EF4444' : undefined }}>{fmt(r.residuo)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      </>}

      {/* LISTINO PREZZI */}
      {tab === 'listino' && (<>{selettoreCiclo}
        <div style={s.formCard}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
            <div style={s.formTitle}>LISTINO PREZZI PACCHETTI</div>
            <button style={s.btnSec} onClick={() => { if (showFormPacchetto) annullaFormPacchetto(); else setShowFormPacchetto(true); }}><Plus size={13}/> Nuovo</button>
          </div>
          {showFormPacchetto && (
            <form onSubmit={salvaNewPacchetto} style={{ background:'var(--bg-input,#1A1A1A)', borderRadius:'8px', padding:'12px', marginBottom:'12px' }}>
              {pacchettoBloccato && (
                <div style={{ display:'flex', alignItems:'center', gap:'6px', background:'#F4600C20', color:'#F4600C', borderRadius:'6px', padding:'8px 10px', marginBottom:'10px', fontSize:'var(--font-size-xs)', fontWeight:'600' }}>
                  <AlertCircle size={14}/> Associato a {pacchettoInModifica.n_associazioni} atleta/i: per non alterare i report non è più modificabile. Puoi solo attivarlo/disattivarlo o cambiarne la visibilità dai pulsanti sulla voce del listino.
                </div>
              )}
              <fieldset disabled={pacchettoBloccato} style={{ border:'none', padding:0, margin:0 }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'8px', marginBottom:'8px' }}>
                <div><label style={s.label}>TIPO *</label>
                  <select style={s.inp} value={formPacchetto.tipo} onChange={e=>setFormPacchetto(p=>({...p,tipo:e.target.value}))}>
                    <option value="CORSO">Corso (abbonamento periodo)</option>
                    <option value="LEZIONI">Lezioni singole (pacchetto n. lezioni)</option>
                  </select>
                </div>
                <div style={{ gridColumn: formPacchetto.tipo === 'LEZIONI' ? '2/-1' : '2/4' }}>
                  <label style={s.label}>NOME PACCHETTO *</label>
                  <input style={s.inp} value={formPacchetto.nome} onChange={e=>setFormPacchetto(p=>({...p,nome:e.target.value}))}
                    placeholder={formPacchetto.tipo === 'LEZIONI' ? 'es. 5 lezioni, 10 lezioni' : 'es. Trimestrale Mono Under18'} required/>
                </div>
                {formPacchetto.tipo === 'CORSO' && (
                  <div><label style={s.label}>IMPORTO € *</label><input type="number" step="0.01" style={s.inp} value={formPacchetto.importo} onChange={e=>setFormPacchetto(p=>({...p,importo:e.target.value}))} required/></div>
                )}
              </div>
              </fieldset>
              {formPacchetto.tipo === 'CORSO' ? (<>
                <div style={{ marginBottom:'8px' }}>
                  <label style={s.label}>SEDI (nessuna selezionata = valido per tutte)</label>
                  {pacchettoBloccato && isRoot && (
                    <div style={{ display:'flex', alignItems:'center', gap:'6px', background:'#F59E0B15', color:'#F59E0B', borderRadius:'6px', padding:'6px 10px', marginBottom:'6px', fontSize:'var(--font-size-xs)', fontWeight:'600' }}>
                      <AlertCircle size={12}/> Sei ROOT: puoi comunque correggere la sede di un pacchetto già in uso (gli altri campi restano bloccati).
                    </div>
                  )}
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'10px', background:'var(--bg-card,#141414)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'6px', padding:'8px 10px' }}>
                    {sediCicloObjs.length === 0 && <span style={{ color:'#555', fontSize:'var(--font-size-xs)' }}>Nessuna sede creata in questo ciclo (vai in Planning Corsi).</span>}
                    {sediCicloObjs.map(sede => (
                      <label key={sede.id} style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'var(--font-size-xs)', cursor: (pacchettoBloccato && !isRoot) ? 'default' : 'pointer', opacity: (pacchettoBloccato && !isRoot) ? 0.5 : 1 }}>
                        <input type="checkbox" disabled={pacchettoBloccato && !isRoot} checked={formPacchetto.sede_ids.includes(sede.id)} onChange={()=>toggleSedePacchetto(sede.id)}/>
                        {sede.nome}
                      </label>
                    ))}
                  </div>
                  <div style={{ fontSize:'10px', color:'#555', marginTop:'2px' }}>Sedi proposte dal ciclo/planning selezionato — puoi flaggarne più di una</div>
                </div>
                <fieldset disabled={pacchettoBloccato} style={{ border:'none', padding:0, margin:0 }}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'8px', marginBottom:'8px' }}>
                  <div><label style={s.label}>CATEGORIA</label>
                    <select style={s.inp} value={formPacchetto.categoria} onChange={e=>setFormPacchetto(p=>({...p,categoria:e.target.value}))}>
                      <option value="UNDER18">Under 18</option>
                      <option value="OPEN">Open</option>
                      <option value="ENTRAMBI">Entrambi</option>
                    </select>
                  </div>
                  <div><label style={s.label}>FREQUENZA</label>
                    <select style={s.inp} value={formPacchetto.frequenza} onChange={e=>setFormPacchetto(p=>({...p,frequenza:e.target.value}))}>
                      <option value="MONO">Monosettimanale</option>
                      <option value="BI">Bisettimanale</option>
                    </select>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', paddingTop:'6px' }}>
                  <input type="checkbox" id="ann" checked={!!formPacchetto.annuale}
                    onChange={e=>setFormPacchetto(p=>({...p,annuale:e.target.checked}))}/>
                  <label htmlFor="ann" style={{ fontSize: 'var(--font-size-sm)', cursor:'pointer' }}>
                    Pacchetto annuale (copre due cicli consecutivi)
                  </label>
                </div>
                </fieldset>
              </>) : (
                <fieldset disabled={pacchettoBloccato} style={{ border:'none', padding:0, margin:0 }}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px', marginBottom:'8px' }}>
                  <div><label style={s.label}>IMPORTO € *</label><input type="number" step="0.01" style={s.inp} value={formPacchetto.importo} onChange={e=>setFormPacchetto(p=>({...p,importo:e.target.value}))} required/></div>
                  <div style={{ gridColumn:'2/-1' }}><label style={s.label}>NOTE (opzionale)</label><input style={s.inp} value={formPacchetto.note} onChange={e=>setFormPacchetto(p=>({...p,note:e.target.value}))} placeholder="es. valide 6 mesi dall'acquisto"/></div>
                </div>
                </fieldset>
              )}
              <div style={{ display:'flex', gap:'8px', justifyContent:'space-between', alignItems:'center', marginTop:'10px' }}>
                {editingPacchettoId && !pacchettoBloccato ? (
                  <button type="button" onClick={() => eliminaPacchetto(pacchettoInModifica)}
                    style={{ background:'none', border:'1px solid #EF4444', color:'#EF4444', borderRadius:'6px', padding:'7px 12px', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' }}>
                    Elimina definitivamente
                  </button>
                ) : <span/>}
                <div style={{ display:'flex', gap:'8px' }}>
                <button type="button" style={s.btnSec} onClick={annullaFormPacchetto}>{pacchettoBloccato ? 'Chiudi' : 'Annulla'}</button>
                {!pacchettoBloccato && (
                  <button type="submit" style={s.btnPri}><Check size={13}/> {editingPacchettoId ? 'Salva modifiche' : 'Crea'}</button>
                )}
                {pacchettoBloccato && isRoot && formPacchetto.tipo === 'CORSO' && (
                  <button type="button" style={s.btnPri} onClick={salvaSedePacchetto}><Check size={13}/> Salva sede</button>
                )}
                </div>
              </div>
            </form>
          )}
          {['CORSO','LEZIONI'].map(tipoGruppo => {
            const delTipo = pacchetti.filter(p => (p.tipo || 'CORSO') === tipoGruppo);
            if (delTipo.length === 0) return null;
            return (
              <div key={tipoGruppo} style={{ marginBottom:'14px' }}>
                <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'800', letterSpacing:'2px', color:'#F4600C', marginBottom:'6px' }}>
                  {tipoGruppo === 'CORSO' ? 'PACCHETTI CORSO' : 'PACCHETTI LEZIONI SINGOLE'}
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                  {delTipo.map(p => (
                    <div key={p.id} onClick={() => startEditPacchetto(p)}
                      title={p.n_associazioni > 0 ? `Associato a ${p.n_associazioni} atleta/i — clicca per i dettagli` : "Clicca per modificare"}
                      style={{ background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'7px 12px', fontSize: 'var(--font-size-xs)', cursor:'pointer', opacity: p.attivo ? 1 : 0.45, display:'flex', alignItems:'center', gap:'8px' }}>
                      <span style={{ fontWeight:'600' }}>{p.nome}</span>
                      <span style={{ color:'#F4600C', fontWeight:'700' }}>{fmt(p.importo)}</span>
                      {tipoGruppo === 'CORSO' && <span style={{ color:'#555' }}>{p.frequenza==='MONO'?'Mono':'Bi'} · {p.categoria} · {p.sedi?.length ? p.sedi.map(s=>s.sede.nome).join(', ') : 'Tutte le sedi'}</span>}
                      {!p.attivo && <span style={{ color:'#EF4444', fontWeight:'700' }}>DISATTIVO</span>}
                      {!p.visibile && <span style={{ color:'#888', fontWeight:'700' }}>NON VISIBILE</span>}
                      {p.n_associazioni > 0 && <span style={{ color:'#F4600C', fontWeight:'700' }} title={`${p.n_associazioni} atleta/i associati`}>🔒 {p.n_associazioni}</span>}
                      <button type="button" title={p.visibile ? 'Nascondi (non selezionabile)' : 'Rendi visibile/selezionabile'}
                        onClick={(e) => { e.stopPropagation(); toggleVisibilePacchetto(p); }}
                        style={{ background:'none', border:'none', cursor:'pointer', color: p.visibile ? '#22C55E' : '#888', padding:0, display:'flex' }}>
                        {p.visibile ? <Eye size={12}/> : <EyeOff size={12}/>}
                      </button>
                      <button type="button" title={p.attivo ? 'Disattiva' : 'Riattiva'}
                        onClick={(e) => { e.stopPropagation(); toggleAttivoPacchetto(p); }}
                        style={{ background:'none', border:'none', cursor:'pointer', color: p.attivo ? '#EF4444' : '#22C55E', padding:0, display:'flex' }}>
                        <X size={12}/>
                      </button>
                      {p.n_associazioni === 0 && (
                        <button type="button" title="Elimina definitivamente"
                          onClick={(e) => { e.stopPropagation(); eliminaPacchetto(p); }}
                          style={{ background:'none', border:'none', cursor:'pointer', color:'#EF4444', padding:0, display:'flex' }}>
                          <Trash2 size={12}/>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {pacchetti.length === 0 && <div style={{ color:'#555', fontSize: 'var(--font-size-sm)', padding:'8px' }}>Nessun pacchetto configurato.</div>}
          <ScontiCorsi sconti={sconti} sediNomi={sediCicloObjs.map(x => x.nome)} puoModificare={isRoot || user?.ruolo === 'ADMIN'} onCambio={caricaSconti}/>
        </div>
      </>)}

      {/* ═══════════════ TAB LEZIONI ═══════════════ */}
      {!contabilita && tab === 'lezioni' && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
            <div style={{ fontSize:'var(--font-size-xs)', color:'#555' }}>
              Lezioni acquistate in più, oltre al pacchetto eventualmente già assegnato nel gruppo
            </div>
            <button style={s.btnPri} onClick={() => setShowFormLezione(v => !v)}>
              <Plus size={13}/> Nuovo acquisto
            </button>
          </div>

          {showFormLezione && (
            <form onSubmit={salvaAcquistoLezioni} style={{ ...s.formCard, marginBottom:'14px' }}>
              <div style={s.formTitle}>NUOVO ACQUISTO LEZIONI</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr auto', gap:'10px', marginTop:'12px', alignItems:'end' }}>
                <div>
                  <label style={s.label}>ATLETA *</label>
                  <select style={s.inp} value={formLezione.atleta_id} onChange={e=>setFormLezione(p=>({...p,atleta_id:e.target.value}))} required>
                    <option value="">— seleziona atleta —</option>
                    {[...atletiAll].sort((a,b)=>a.cognome.localeCompare(b.cognome)).map(a => (
                      <option key={a.id} value={a.id}>{a.cognome} {a.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={s.label}>PACCHETTO *</label>
                  <select style={s.inp} value={formLezione.pacchetto_id} onChange={e=>setFormLezione(p=>({...p,pacchetto_id:e.target.value}))} required>
                    <option value="">— seleziona —</option>
                    {pacchettiLezioni.map(p => (
                      <option key={p.id} value={p.id}>{p.nome} — {fmt(p.importo)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={s.label}>NOTE</label>
                  <input style={s.inp} value={formLezione.note} onChange={e=>setFormLezione(p=>({...p,note:e.target.value}))} placeholder="Opzionale"/>
                </div>
                <div style={{ display:'flex', gap:'4px' }}>
                  <button type="submit" style={s.btnPri}><Check size={13}/> Salva</button>
                  <button type="button" style={s.btnSec} onClick={()=>setShowFormLezione(false)}><X size={13}/></button>
                </div>
              </div>
            </form>
          )}

          {acquistiLezioni.length === 0 ? (
            <div style={{ color:'#555', fontSize:'var(--font-size-sm)', padding:'30px', textAlign:'center' }}>
              Nessun acquisto lezioni registrato.
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {acquistiLezioni.map(acq => {
                const pagato = acq.pagamenti.reduce((s,p)=>s+p.importo,0);
                const dovuto = acq.pacchetto.importo;
                const saldo = dovuto - pagato;
                const isOpen = apertaLezione === acq.id;
                return (
                  <div key={acq.id} style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'10px', overflow:'hidden' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 14px', cursor:'pointer' }}
                      onClick={() => setApertaLezione(isOpen ? null : acq.id)}>
                      {isOpen ? <ChevronDown size={13} color="#555"/> : <ChevronRight size={13} color="#555"/>}
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:'700', fontSize:'var(--font-size-sm)' }}>{acq.atleta.cognome} {acq.atleta.nome}</div>
                        <div style={{ fontSize:'var(--font-size-xs)', color:'#555', marginTop:'2px' }}>{acq.pacchetto.nome}</div>
                      </div>
                      <div style={{ textAlign:'right', minWidth:'80px' }}>
                        <div style={{ fontFamily:"'Barlow Condensed'", fontSize:'var(--font-size-lg)', fontWeight:'800', color: saldo<=0?'#22C55E':'#F4600C' }}>
                          {saldo <= 0 ? '✓ Saldato' : fmt(saldo)}
                        </div>
                        <div style={{ fontSize:'var(--font-size-xs)', color:'#555' }}>
                          {fmt(pagato)} / {fmt(dovuto)}
                        </div>
                      </div>
                      <button style={{ ...s.iconBtn, color:'#EF4444' }} onClick={e=>{e.stopPropagation();eliminaAcquistoLezioni(acq.id);}}>
                        <X size={12}/>
                      </button>
                    </div>

                    {isOpen && (
                      <div style={{ borderTop:'1px solid var(--border,#1E1E1E)', padding:'10px 14px 12px 38px' }}>
                        {showFormPagLez === acq.id && (
                          <div style={{ background:'var(--bg-input,#1A1A1A)', borderRadius:'8px', padding:'10px', marginBottom:'10px' }}>
                            <div style={{ fontSize:'var(--font-size-xs)', fontWeight:'700', color:'#F4600C', marginBottom:'8px' }}>NUOVO PAGAMENTO</div>
                            <div style={{ display:'grid', gridTemplateColumns:'110px 140px 130px 1fr auto', gap:'8px', alignItems:'end' }}>
                              <div><label style={s.label}>IMPORTO € *</label><input type="number" step="0.01" style={s.inp} value={formPagLez.importo} onChange={e=>setFormPagLez(p=>({...p,importo:e.target.value}))} autoFocus/></div>
                              <div><label style={s.label}>DATA</label><input type="date" style={s.inp} value={formPagLez.data_pagamento} onChange={e=>setFormPagLez(p=>({...p,data_pagamento:e.target.value}))}/></div>
                              <div><label style={s.label}>METODO</label>
                                <select style={s.inp} value={formPagLez.metodo} onChange={e=>setFormPagLez(p=>({...p,metodo:e.target.value}))}>
                                  {METODI.map(m=><option key={m} value={m}>{NOME_METODO[m]}</option>)}
                                </select>
                              </div>
                              <div><label style={s.label}>NOTE</label><input style={s.inp} value={formPagLez.note} onChange={e=>setFormPagLez(p=>({...p,note:e.target.value}))} placeholder="Opzionale"/></div>
                              <div style={{ display:'flex', gap:'4px' }}>
                                <button style={s.btnPri} onClick={()=>salvaPagamentoLezione(acq.id)} disabled={!formPagLez.importo}><Check size={13}/></button>
                                <button style={s.btnSec} onClick={()=>setShowFormPagLez(null)}><X size={13}/></button>
                              </div>
                            </div>
                          </div>
                        )}
                        {acq.pagamenti.length > 0 ? (
                          <div style={{ display:'flex', flexDirection:'column', gap:'4px', marginBottom:'8px' }}>
                            {acq.pagamenti.map(p => (
                              <div key={p.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'5px 8px', background:'var(--bg-card,#141414)', borderRadius:'6px', fontSize:'var(--font-size-sm)' }}>
                                <span style={{ color:'#22C55E', fontFamily:"'Barlow Condensed'", fontSize:'var(--font-size-lg)', fontWeight:'700', minWidth:'65px' }}>{fmt(p.importo)}</span>
                                <span style={{ color:'#555', fontSize:'var(--font-size-xs)' }}>{fmtData(p.data_pagamento)}</span>
                                <span style={{ background:'var(--bg-input,#1A1A1A)', borderRadius:'4px', padding:'1px 8px', fontSize:'var(--font-size-xs)', color:'#888' }}>{NOME_METODO[p.metodo] || p.metodo}</span>
                                {p.note && <span style={{ color:'#555', fontSize:'var(--font-size-xs)', flex:1 }}>{p.note}</span>}
                                <button style={{ ...s.iconBtn, color:'#EF4444', marginLeft:'auto' }} onClick={()=>eliminaPagamentoLezione(p.id)}><X size={11}/></button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize:'var(--font-size-xs)', color:'#555', padding:'4px 0 8px' }}>Nessun pagamento registrato.</div>
                        )}
                        {showFormPagLez !== acq.id && (
                          <button style={{ ...s.btnPri, padding:'5px 10px', fontSize:'var(--font-size-xs)' }}
                            onClick={()=>{ setShowFormPagLez(acq.id); setFormPagLez({ importo:'', metodo:'CONTANTI', data_pagamento: new Date().toISOString().slice(0,10), note:'' }); }}>
                            <Plus size={12}/> Aggiungi pagamento
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════ TAB CORSI ═══════════════ */}
      {!contabilita && tab === 'corsisti' && <>

      {selettoreCiclo}

      {conPiano.length > 0 && (
        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center', fontSize:'var(--font-size-xs)', margin:'-6px 0 14px' }}>
          <span style={{ color:'var(--text-muted)' }}>Piani rate: {conPiano.length} corsisti con scadenze</span>
          {inRitardo.length > 0 && <button onClick={() => setFiltroRate('IN_RITARDO')} style={{ ...s.btnSec, padding:'4px 10px', color:'#DC2626' }}>⚠ {inRitardo.length} in ritardo</button>}
          {inArrivo.length > 0 && <button onClick={() => setFiltroRate('IN_ARRIVO')} style={{ ...s.btnSec, padding:'4px 10px', color:'#B45309' }}>{inArrivo.length} con rata entro 7 giorni</button>}
        </div>
      )}

      {/* FILTRI */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap' }}>
        <input style={{ ...s.filterSel, flex:1, minWidth:'180px' }} placeholder="Cerca per nome..." value={filtroTesto} onChange={e=>setFiltroTesto(e.target.value)}/>
        <select style={s.filterSel} value={filtroSede} onChange={e=>setFiltroSede(e.target.value)}>
          <option value="">Tutte le sedi</option>
          {sediCiclo.map(n=><option key={n} value={n}>{n}</option>)}
        </select>
        <select style={s.filterSel} value={filtroStato} onChange={e=>setFiltroStato(e.target.value)}>
          <option value="">Tutti gli stati</option>
          <option value="SALDATO">Saldati</option>
          <option value="PARZIALE">Parziale</option>
          <option value="DA_PAGARE">Da pagare</option>
          <option value="RIMOSSO">Tolti dal gruppo</option>
        </select>
        <select style={s.filterSel} value={filtroRate} onChange={e=>setFiltroRate(e.target.value)}>
          <option value="">Rate: tutte</option>
          <option value="IN_RITARDO">Rata in ritardo</option>
          <option value="IN_ARRIVO">Rata entro 7 giorni</option>
          <option value="IN_REGOLA">Rate in regola</option>
          <option value="SALDATO">Rate saldate</option>
          <option value="SENZA">Senza piano rate</option>
        </select>
        <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'var(--font-size-xs)', cursor:'pointer' }} title="Per la detrazione del 19% ai minori la ricevuta dovrà riportare il genitore che paga (da sistemare)">
          <input type="checkbox" checked={soloU18} onChange={e=>setSoloU18(e.target.checked)} style={{ accentColor:'#F4600C' }}/> Solo under 18 ({corsisti.filter(c => minorenni.has(c.atleta_id)).length})
        </label>
        <span style={{ display:'flex', alignItems:'center', fontSize: 'var(--font-size-xs)', color:'#555' }}>{corsistiFiltrati.length} corsisti</span>
      </div>

      {/* TABELLA CORSISTI */}
      <div style={{ overflowX:'auto' }}>
        <table className="tabella-schede" style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'var(--bg-input,#1A1A1A)' }}>
              <th style={s.th}></th>
              <th style={s.th}>CORSISTA</th>
              <th style={s.th}>SEDE · SLOT</th>
              <th style={s.th}>PACCHETTO</th>
              <th style={s.th}>DOVUTO</th>
              <th style={s.th}>PAGATO</th>
              <th style={s.th}>SALDO</th>
              <th style={s.th}>STATO</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} style={{ textAlign:'center', padding:'30px', color:'#555' }}>Caricamento...</td></tr>}
            {righePagina.map(c => {
              const sp = STATO_PAG[c.stato_pagamento];
              const isOpen = aperto === c.id;
              const rimosso = c.rimosso;
              return (
                <React.Fragment key={c.id}>
                  <tr style={{ borderBottom:'1px solid var(--border,#1E1E1E)', cursor:'pointer', opacity: rimosso ? 0.55 : 1 }}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--bg-input,#1A1A1A)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                    onClick={() => setAperto(isOpen ? null : c.id)}>
                    <td className="td-via" style={{ ...s.td, width:'30px', textAlign:'center', color:'#555' }}>
                      {isOpen ? <ChevronDown size={13}/> : <ChevronRight size={13}/>}
                    </td>
                    <td style={s.td}>
                      <div style={{ fontWeight:'600', fontSize: 'var(--font-size-sm)', textDecoration: rimosso ? 'line-through' : 'none', display:'flex', alignItems:'center', gap:'6px' }}>
                        {c.atleta.cognome} {c.atleta.nome}
                        {rimosso && <span style={{ fontSize:'var(--font-size-xs)', background:'#EF444420', color:'#EF4444', borderRadius:'4px', padding:'1px 6px', textDecoration:'none', fontWeight:'600' }}>Rimosso</span>}
                        {minorenni.has(c.atleta_id) && <span title="Minorenne: per la detrazione 19% servirà il genitore pagante in ricevuta" style={{ fontSize:'var(--font-size-xs)', background:'#A855F720', color:'#7E3AF2', borderRadius:'4px', padding:'1px 6px', fontWeight:'700' }}>U18</span>}
                        {isMobile && <span style={{ marginLeft:'auto', color:'var(--text-dim)', flexShrink:0 }}>{isOpen ? <ChevronDown size={18}/> : <ChevronRight size={18}/>}</span>}
                      </div>
                      <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>{c.atleta.categoria==='UNDER18'?'Under 18':'Open'}{c.atleta.cellulare?` · ${c.atleta.cellulare}`:''}</div>
                      {c.atleta.rinominato_da && <div style={{ fontSize: 'var(--font-size-xs)', color:'#854F0B' }}>(prima: {c.atleta.rinominato_da})</div>}
                    </td>
                    <td style={s.td} data-label="Sede · turno">
                      {(c.turni || []).map(t => (
                        <div key={t.id} style={{ fontSize:'var(--font-size-xs)', color: t.rimosso ? '#555' : 'inherit', textDecoration: t.rimosso ? 'line-through' : 'none' }}
                          title={t.rimosso ? `Tolto il ${t.rimosso_at ? new Date(t.rimosso_at).toLocaleDateString('it-IT') : '—'}${t.motivo_rimozione ? ` — ${t.motivo_rimozione}` : ''}` : t.doppio && t.doppio_approvato ? 'Secondo turno (doppio)' : ''}>
                          {t.sede} · {t.giorno?.charAt(0)+t.giorno?.slice(1).toLowerCase()} {t.orario}
                          {!t.rimosso && t.doppio && t.doppio_approvato && <span style={{ color:'#185FA5', marginLeft:'4px' }}>↔</span>}
                          {t.rimosso && t.motivo_rimozione && <span style={{ marginLeft:'4px', fontStyle:'italic' }}>({t.motivo_rimozione})</span>}
                        </div>
                      ))}
                    </td>
                    <td style={s.td} data-label="Pacchetto">
                      <select style={{ ...s.filterSel, fontSize: 'var(--font-size-xs)', padding:'4px 8px', maxWidth:'180px' }}
                        value={c.pacchetto_id || ''}
                        onClick={e=>e.stopPropagation()}
                        onChange={e=>assegnaPacchetto(c.id, e.target.value ? parseInt(e.target.value) : null)}>
                        <option value="">— nessun pacchetto —</option>
                        {pacchetti.filter(p => (!p.sede_ids?.length || p.sede_ids.includes(c.slot.sede.id)) && (p.id === c.pacchetto_id || (p.attivo && p.visibile))).map(p => (
                          <option key={p.id} value={p.id}>{p.nome} — {fmt(p.importo)}{(p.tipo === 'LEZIONI') ? ' (Lezioni)' : ''}</option>
                        ))}
                      </select>
                      {c.pacchetto?.annuale && !c.pacchetto_annuale_ref && (() => {
                        // Sconto da regolamento (solo annuali, non cumulabili): vale se non c'è un prezzo personalizzato
                        const lista = sconti.filter(sc => sc.id === c.sconto_id || (sc.attivo && (!sc.sedi?.length || sc.sedi.includes(c.slot.sede.nome))));
                        if (!lista.length && !c.sconto_id) return <div style={{ marginTop:'4px', fontSize:'var(--font-size-xs)', color:'var(--text-dim,#888)' }} title="Si aggiunge la sede nel riquadro SCONTI sotto il Listino">Nessuno sconto per {c.slot.sede.nome}</div>;
                        const finale = (pct) => Math.round(c.pacchetto.importo * (100 - pct)) / 100;
                        return (
                          <div style={{ marginTop:'4px', fontSize:'var(--font-size-xs)' }} onClick={e=>e.stopPropagation()}>
                            {puoFare(user, 'pagamento_corsi', 'edit') ? (
                              <select style={{ ...s.filterSel, fontSize:'var(--font-size-xs)', padding:'3px 8px', maxWidth:'260px', color: c.sconto_id ? '#15803D' : undefined }}
                                value={c.sconto_id || ''} onChange={e => assegnaSconto(c.id, e.target.value ? +e.target.value : null)}>
                                <option value="">Nessuno sconto</option>
                                {lista.map(sc => <option key={sc.id} value={sc.id}>−{sc.percentuale}% {sc.nome} → {fmt(finale(sc.id === c.sconto_id ? c.sconto_pct : sc.percentuale))}</option>)}
                              </select>
                            ) : c.sconto_id ? (
                              <span style={{ color:'#15803D', fontWeight:600 }}>−{c.sconto_pct}% {c.sconto?.nome} → {fmt(finale(c.sconto_pct))}</span>
                            ) : null}
                            {c.sconto_id && c.importo_personalizzato != null && <div style={{ color:'#B45309', marginTop:'2px' }}>Non cumulabile: vale il prezzo personalizzato</div>}
                          </div>
                        );
                      })()}
                      {c.pacchetto && !c.pacchetto_annuale_ref && (
                        <div style={{ display:'flex', alignItems:'center', gap:'6px', marginTop:'4px', fontSize:'var(--font-size-xs)' }} onClick={e=>e.stopPropagation()}>
                          {c.importo_personalizzato != null
                            ? <span style={{ color:'#7E3AF2', fontWeight:'600' }} title={`Motivo: ${c.motivo_importo || '—'}${c.importo_modificato_da ? ` · da ${c.importo_modificato_da}` : ''}`}>Prezzo per lui/lei {fmt(c.importo_personalizzato)} <span style={{ color:'var(--text-muted,#888)', fontWeight:400 }}>(listino {fmt(c.pacchetto.importo)} · {c.motivo_importo})</span></span>
                            : null}
                          {puoFare(user, 'pagamento_corsi', 'edit') && (
                            <button style={{ ...s.iconBtn, padding:'1px 6px', fontSize:'var(--font-size-xs)' }} title="Prezzo diverso per questa persona (sconto)" onClick={()=>cambiaImporto('pacchetto', c.id, c.importo_personalizzato, c.pacchetto.importo)}>
                              <Pencil size={10}/> {c.importo_personalizzato != null ? 'prezzo' : 'prezzo diverso'}
                            </button>
                          )}
                        </div>
                      )}
                      {c.avvisi?.map((a, i) => (
                        <div key={i} style={{ fontSize:'var(--font-size-xs)', color:'#B7791F', marginTop:'3px' }}>⚠ {a}</div>
                      ))}
                      {c.quota_tesseramento && (
                        <div style={{ display:'flex', alignItems:'center', gap:'6px', marginTop:'4px', fontSize:'var(--font-size-xs)' }} onClick={e=>e.stopPropagation()}>
                          {c.quota_tesseramento.esonerato ? (
                            <span style={{ color:'var(--text-muted,#888)' }} title={`Esonerato da ${c.quota_tesseramento.esonerato_da || '—'}`}>
                              Tesseramento esonerato: <em>{c.quota_tesseramento.nota_esonero}</em>
                            </span>
                          ) : (
                            <span style={{ color:'#F4600C', fontWeight:'600' }} title={c.quota_tesseramento.importo_personalizzato != null ? `Quota del ciclo ${fmt(c.quota_tesseramento.importo)} · motivo: ${c.quota_tesseramento.motivo_importo || '—'}` : undefined}>
                              + Tesseramento {fmt(importoQuota(c.quota_tesseramento))}{c.quota_tesseramento.importo_personalizzato != null && <span style={{ color:'#7E3AF2' }}> (personalizzato)</span>}
                            </span>
                          )}
                          {puoFare(user, 'pagamento_corsi', 'edit') && !c.quota_tesseramento.esonerato && (
                            <button style={{ ...s.iconBtn, padding:'1px 6px', fontSize:'var(--font-size-xs)' }} title="Importo diverso per questa persona" onClick={()=>cambiaImporto('quota', c.quota_tesseramento.id, c.quota_tesseramento.importo_personalizzato, c.quota_tesseramento.importo)}>
                              <Pencil size={10}/>
                            </button>
                          )}
                          {puoFare(user, 'pagamento_corsi', 'edit') && (
                            <button style={{ ...s.iconBtn, padding:'1px 6px', fontSize:'var(--font-size-xs)' }} onClick={()=>esoneraQuota(c.quota_tesseramento)}>
                              {c.quota_tesseramento.esonerato ? 'ripristina' : 'esonera'}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="td-terzo" data-label="Dovuto" style={{ ...s.td, textAlign:'right', fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'700' }}>
                      {c.totale_dovuto > 0 ? fmt(c.totale_dovuto) : '—'}
                    </td>
                    <td className="td-terzo" data-label="Pagato" style={{ ...s.td, textAlign:'right', fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'700', color:'#22C55E' }}>
                      {c.totale_pagato > 0 ? fmt(c.totale_pagato) : '—'}
                    </td>
                    <td className="td-terzo" data-label="Saldo" style={{ ...s.td, textAlign:'right', fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'700', color: c.saldo > 0 ? '#EF4444' : c.saldo === 0 && c.totale_dovuto > 0 ? '#22C55E' : '#555' }}>
                      {c.stato_pagamento === 'ANNUALE_PRECEDENTE' ? (
                        <div style={{ color:'#185FA5', fontSize: 'var(--font-size-sm)' }}>∞ incluso</div>
                      ) : c.stato_pagamento === 'DOPPIO_INCLUSO' ? (
                        <div style={{ color:'#185FA5', fontSize: 'var(--font-size-sm)' }}>↔ incluso</div>
                      ) : (
                        c.totale_dovuto > 0 ? (c.saldo === 0 ? '✓' : fmt(c.saldo)) : '—'
                      )}
                    </td>
                    <td style={s.td}>
                      {c.stato_pagamento === 'ANNUALE_PRECEDENTE' ? (
                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', padding:'3px 9px', borderRadius:'99px', background:'#3B82F620', color:'#185FA5' }}>
                          ∞ Annuale
                        </span>
                      ) : (
                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', padding:'3px 9px', borderRadius:'99px', background:sp.bg, color:sp.color }}>
                          {sp.icon} {sp.label}
                        </span>
                      )}
                      {!rimosso && c.piano_rate && c.piano_rate.stato !== 'SALDATO' && (() => {
                        const pr = c.piano_rate, st = STATO_RATE[pr.stato];
                        return (
                          <div style={{ fontSize:'var(--font-size-xs)', fontWeight:600, color: st.color, marginTop:'4px', whiteSpace:'nowrap' }}>
                            {pr.stato === 'IN_RITARDO' ? `⚠ mancano ${fmt(pr.mancante_scaduto)} scaduti`
                              : pr.prossima ? `${pr.prossima.numero}ª rata ${fmt(pr.prossima.manca)} entro ${ggIso(pr.prossima.scadenza)}` : st.label}
                          </div>
                        );
                      })()}
                      {!rimosso && c.saldo > 0 && bonificiTrovati[c.id] > 0 && (
                        <div title="Apri la riga: in alto trovi il bonifico da collegare" style={{ display:'inline-block', marginTop:'4px', fontSize:'var(--font-size-xs)', fontWeight:700, padding:'2px 8px', borderRadius:'99px', background:'#1D4ED815', color:'#1D4ED8', whiteSpace:'nowrap' }}>
                          🏦 {bonificiTrovati[c.id] === 1 ? 'bonifico trovato' : `${bonificiTrovati[c.id]} bonifici trovati`}
                        </div>
                      )}
                    </td>
                    <td className="td-azioni" style={{ ...s.td, whiteSpace:'nowrap' }} onClick={e=>e.stopPropagation()}>
                      {!rimosso && c.stato_pagamento !== 'ANNUALE_PRECEDENTE' && c.stato_pagamento !== 'DOPPIO_INCLUSO' && (
                        <button style={{ ...s.btnPri, padding:'5px 10px', fontSize: 'var(--font-size-xs)' }}
                          onClick={() => { setShowFormPag(c.id); setAperto(c.id); }}>
                          <Plus size={12}/> Pagamento
                        </button>
                      )}
                      {!rimosso && c.totale_dovuto > 0 && (
                        <button style={{ ...s.btnSec, padding:'5px 10px', fontSize:'var(--font-size-xs)', marginLeft:'4px' }}
                          title={numeroRicevuta[c.atleta_id] ? `Ricevuta n. ${numeroRicevuta[c.atleta_id].numero}` : 'Genera la ricevuta (riceve il numero progressivo)'}
                          onClick={() => setRicevutaAperta({ c, definitiva: false })}>
                          <FileText size={12}/> {numeroRicevuta[c.atleta_id] ? numeroRicevuta[c.atleta_id].numero : 'Ricevuta'}
                        </button>
                      )}
                    </td>
                  </tr>

                  {/* RIGA ESPANSA */}
                  {isOpen && (
                    <tr className="riga-dettaglio" style={{ background:'var(--bg-input,#1A1A1A)' }}>
                      <td colSpan={9} style={{ padding:'0 14px 12px 44px' }}>

                        {/* BONIFICI DELLA BANCA: in alto, così il caso più comune è un clic */}
                        {puoCollegare && !rimosso && c.totale_dovuto > 0 && c.saldo > 0 && (
                          <BonificiProposti corsista={c} puoCollegare={puoCollegare} onCollegato={loadCorsisti}/>
                        )}

                        {/* FORM NUOVO PAGAMENTO */}
                        {showFormPag === c.id && (
                          <div style={{ background:'var(--bg-card,#141414)', border:'1px solid #F4600C40', borderRadius:'8px', padding:'12px', marginTop:'10px', marginBottom:'8px' }}>
                            <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', color:'#F4600C', marginBottom:'8px' }}>NUOVO PAGAMENTO</div>
                            <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr) minmax(0,1fr)' : '120px 140px 140px 1fr auto', gap:'8px', alignItems:'end' }}>
                              <div><label style={s.label}>IMPORTO € *</label><input type="number" step="0.01" style={s.inp} value={formPag.importo} onChange={e=>setFormPag(p=>({...p,importo:e.target.value}))} autoFocus/></div>
                              <div><label style={s.label}>DATA</label><input type="date" style={s.inp} value={formPag.data_pagamento} onChange={e=>setFormPag(p=>({...p,data_pagamento:e.target.value}))}/></div>
                              <div><label style={s.label}>METODO</label>
                                <select style={s.inp} value={formPag.metodo} onChange={e=>setFormPag(p=>({...p,metodo:e.target.value}))}>
                                  {METODI.map(m=><option key={m} value={m}>{NOME_METODO[m]}</option>)}
                                </select>
                              </div>
                              <div><label style={s.label}>NOTE</label><input style={s.inp} value={formPag.note} onChange={e=>setFormPag(p=>({...p,note:e.target.value}))} placeholder="Opzionale"/></div>
                              <div style={{ display:'flex', gap:'4px' }}>
                                <button style={s.btnPri} onClick={()=>salvaPagamento(c.id)} disabled={!formPag.importo}><Check size={13}/></button>
                                <button style={s.btnSec} onClick={()=>setShowFormPag(null)}><X size={13}/></button>
                              </div>
                            </div>
                            {formPag.metodo === 'CONTANTI' && (
                              <div style={{ fontSize:'var(--font-size-xs)', color:'#B45309', marginTop:'8px' }}>
                                💶 Va da solo anche nella cassa Contanti (Movimenti bancari → Contanti), già collegato. Se qualcuno l'ha già scritto in cassa (stesso importo e cognome, entro 30 giorni), si aggancia a quello: non scriverlo due volte.
                              </div>
                            )}
                          </div>
                        )}

                        {/* STORICO PAGAMENTI */}
                        {c.pagamenti?.length > 0 ? (
                          <div style={{ marginTop:'8px' }}>
                            <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'6px' }}>STORICO PAGAMENTI</div>
                            <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
                              {c.pagamenti.map(p => (
                                <div key={p.id} style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:'12px', padding:'6px 10px', background:'var(--bg-card,#141414)', borderRadius:'6px', fontSize: 'var(--font-size-sm)' }}>
                                  <span style={{ color:'#22C55E', fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'700', minWidth:'70px' }}>{fmt(p.importo)}</span>
                                  <span style={{ color:'#555', fontSize: 'var(--font-size-xs)' }}>{fmtData(p.data_pagamento)}</span>
                                  <span style={{ background:'var(--bg-input,#1A1A1A)', borderRadius:'4px', padding:'1px 8px', fontSize: 'var(--font-size-xs)', color:'#888' }}>{NOME_METODO[p.metodo] || p.metodo}</span>
                                  {p.turno && <span style={{ color:'#555', fontSize: 'var(--font-size-xs)' }} title="Registrato su un altro turno dello stesso atleta">({p.turno})</span>}
                                  {p.bank_transaction_id && (p.metodo === 'CONTANTI'
                                    ? <span title={`Registrato nella cassa Contanti${p.collegato_da ? ` (${p.collegato_da})` : ''}`} style={{ background:'#F59E0B1F', color:'#B45309', borderRadius:'4px', padding:'1px 6px', fontSize:'var(--font-size-xs)', fontWeight:700 }}>💶 in cassa</span>
                                    : <span title={`Collegato al movimento della banca${p.collegato_da ? ` da ${p.collegato_da}` : ''}`} style={{ background:'#3B82F61A', color:'#1D4ED8', borderRadius:'4px', padding:'1px 6px', fontSize:'var(--font-size-xs)', fontWeight:700 }}>🏦 banca</span>)}
                                  {p.note && <span style={{ color:'#555', fontSize: 'var(--font-size-xs)', flex:1 }}>{p.note}</span>}
                                  {p.avvisi_piano?.length > 0 && <span style={{ color:'#B45309', fontSize:'var(--font-size-xs)', flexBasis:'100%' }}>⚠ {p.avvisi_piano.join(' · ')}</span>}
                                  <button style={{ ...s.iconBtn, color:'#EF4444', marginLeft:'auto' }} onClick={()=>eliminaPagamento(p.id)}><X size={11}/></button>
                                </div>
                              ))}
                            </div>
                            {/* RIEPILOGO */}
                            <div style={{ display:'flex', gap:'16px', marginTop:'8px', padding:'6px 10px', background:'var(--bg-card,#141414)', borderRadius:'6px', fontSize: 'var(--font-size-xs)' }}>
                              <span>Dovuto: <strong>{fmt(c.totale_dovuto)}</strong>
                                {importoQuota(c.quota_tesseramento) > 0 && c.pacchetto && ` (${fmt(c.totale_dovuto - importoQuota(c.quota_tesseramento))} pacchetto + ${fmt(importoQuota(c.quota_tesseramento))} tesseramento)`}
                              </span>
                              <span style={{ color:'#22C55E' }}>Pagato: <strong>{fmt(c.totale_pagato)}</strong></span>
                              <span style={{ color: c.saldo > 0 ? '#EF4444' : '#22C55E' }}>Saldo: <strong>{c.saldo > 0 ? fmt(c.saldo) : '✓ Saldato'}</strong></span>
                            </div>
                          </div>
                        ) : (
                          <div style={{ fontSize: 'var(--font-size-xs)', color:'#555', padding:'8px 0' }}>
                            {(c.pacchetto || c.totale_dovuto > 0) ? 'Nessun pagamento registrato ancora.' : 'Assegna un pacchetto per iniziare a tracciare i pagamenti.'}
                          </div>
                        )}

                        {/* PIANO RATE */}
                        {c.piano_rate && (
                          <div style={{ marginTop:'12px' }}>
                            <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'6px' }}>
                              PIANO RATE <span style={{ ...s.badgeRate, background: STATO_RATE[c.piano_rate.stato].bg, color: STATO_RATE[c.piano_rate.stato].color }}>{STATO_RATE[c.piano_rate.stato].label}</span>
                            </div>
                            <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                              {c.piano_rate.rate.map(r => {
                                const oggi = new Date().toISOString().slice(0,10);
                                const scaduta = !r.pagata_il && r.scadenza < oggi;
                                return (
                                  <div key={r.numero} style={{ background:'var(--bg-card)', borderRadius:'8px', padding:'6px 10px', fontSize:'var(--font-size-xs)', border: scaduta ? '1px solid #EF444466' : '1px solid var(--border)' }}>
                                    <b>{r.numero}ª</b> {fmt(r.importo)} · entro {ggIso(r.scadenza)}
                                    <div style={{ color: r.pagata_il ? (r.pagata_il > r.scadenza ? '#B45309' : '#16A34A') : scaduta ? '#DC2626' : 'var(--text-muted)', fontWeight:600 }}>
                                      {r.pagata_il ? `pagata il ${ggIso(r.pagata_il)}${r.pagata_il > r.scadenza ? ' (in ritardo)' : ''}` : scaduta ? 'non pagata, scaduta' : 'da pagare'}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {corsistiFiltrati.length === 0 && !loading && (
              <tr><td colSpan={9} style={{ textAlign:'center', padding:'40px', color:'#555' }}>
                Nessun corsista trovato. Configura il planning e conferma gli atleti.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {nPagine > 1 && (() => {
        const vai = (n) => { setPagina(n); setAperto(null); window.scrollTo({ top: 0, behavior: 'smooth' }); };
        const btn = (dis) => ({ ...s.filterSel, cursor: dis ? 'default' : 'pointer', opacity: dis ? 0.4 : 1, fontWeight:600 });
        return (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', flexWrap:'wrap', padding:'14px 0' }}>
            <button style={btn(paginaOk === 0)} disabled={paginaOk === 0} onClick={() => vai(0)}>«</button>
            <button style={btn(paginaOk === 0)} disabled={paginaOk === 0} onClick={() => vai(paginaOk - 1)}>‹ Indietro</button>
            <select style={s.filterSel} value={paginaOk} onChange={e => vai(+e.target.value)}>
              {Array.from({ length: nPagine }, (_, i) => (
                <option key={i} value={i}>Pagina {i + 1} di {nPagine} · {i * PER_PAGINA + 1}–{Math.min((i + 1) * PER_PAGINA, corsistiFiltrati.length)}</option>
              ))}
            </select>
            <button style={btn(paginaOk >= nPagine - 1)} disabled={paginaOk >= nPagine - 1} onClick={() => vai(paginaOk + 1)}>Avanti ›</button>
            <button style={btn(paginaOk >= nPagine - 1)} disabled={paginaOk >= nPagine - 1} onClick={() => vai(nPagine - 1)}>»</button>
          </div>
        );
      })()}
      </> /* fine tab corsisti */}
      {ricevutaAperta && (() => {
        // prima si guarda (anteprima senza numero), il numero arriva solo con "Conferma e genera"
        const c = ricevutaAperta.c, numerata = !!numeroRicevuta[c.atleta_id] || ricevutaAperta.definitiva;
        return (
          <AnteprimaDocumento
            doc={{ nome: `Ricevuta ${c.atleta.cognome} ${c.atleta.nome}`, file_nome: 'ricevuta.pdf', tipo: 'Ricevuta' }}
            endpoint={numerata ? `/ricevute/pdf/${c.atleta_id}/${cicloId}` : `/ricevute/anteprima/${c.atleta_id}/${cicloId}`}
            avviso={numerata ? null : 'Anteprima: la ricevuta non ha ancora un numero. Controlla i dati e premi "Conferma e genera" per assegnarle il numero progressivo.'}
            azioni={numerata ? null : <button style={{ ...s.btnPri, padding:'6px 12px', fontSize:'var(--font-size-xs)' }} onClick={() => setRicevutaAperta(r => ({ ...r, definitiva: true }))}><Check size={13}/> Conferma e genera</button>}
            onClose={() => { setRicevutaAperta(null); loadRicevute(); }} />
        );
      })()}
    </div>
  );
}

const s = {
  title:     { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800', letterSpacing:'2px' },
  sub:       { color:'var(--text-muted,#888)', fontSize: 'var(--font-size-sm)', marginTop:'4px' },
  btnPri:    { display:'flex', alignItems:'center', gap:'6px', background:'#F4600C', color:'#fff', border:'none', borderRadius:'8px', padding:'9px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  btnSec:    { display:'flex', alignItems:'center', gap:'6px', background:'var(--bg-input,#1A1A1A)', color:'var(--text,#fff)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'8px 12px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  iconBtn:   { background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'6px', padding:'5px 7px', color:'var(--text-muted,#888)', cursor:'pointer', display:'inline-flex', alignItems:'center' },
  filterSel: { background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'8px 12px', color:'var(--text,#fff)', fontSize: 'var(--font-size-xs)', outline:'none' },
  formCard:  { background:'var(--bg-card,#141414)', border:'1px solid #F4600C40', borderRadius:'12px', padding:'16px', marginBottom:'14px' },
  formTitle: { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-sm)', fontWeight:'800', color:'#F4600C', marginBottom:'0' },
  secLabel:  { fontSize: 'var(--font-size-xs)', fontWeight:'600', color:'#555' },
  label:     { fontSize: 'var(--font-size-xs)', letterSpacing:'2px', fontWeight:'700', color:'#555', display:'block', marginBottom:'4px' },
  inp:       { background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'8px 12px', color:'var(--text,#fff)', fontSize: 'var(--font-size-sm)', outline:'none', width:'100%', boxSizing:'border-box' },
  th:        { padding:'10px 12px', textAlign:'left', fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'1px', color:'#555', borderBottom:'1px solid var(--border,#1E1E1E)', whiteSpace:'nowrap' },
  td:        { padding:'10px 12px', fontSize: 'var(--font-size-sm)', verticalAlign:'middle' },
  kpi:       { background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'10px', padding:'12px 14px' },
  kpiV:      { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-xl)', fontWeight:'800', color:'var(--text,#fff)' },
  badgeRate: { fontSize:'var(--font-size-xs)', fontWeight:700, letterSpacing:0, padding:'2px 8px', borderRadius:'99px', marginLeft:'6px' },
  kpiL:      { fontSize: 'var(--font-size-xs)', color:'#555', marginTop:'2px' },
};
